import { z } from 'zod';
import type { GameId, PlayerId } from './ids';
import type { RoomVisibility } from './realtime-events';

/** Friends and visits (ADR 0019). The limits keep a stranger from collecting children. */
export const MAX_FRIENDS = 50;
/** Unanswered requests a player may have sent at once. */
export const MAX_OPEN_REQUESTS = 10;
/** An unanswered request is gone after this long, and a "no" keeps the same request away as long. */
export const FRIEND_REQUEST_TTL_MS = 7 * 24 * 60 * 60_000;
/** How long a friend in a room with a code has to let a knocking friend in. */
export const KNOCK_TIMEOUT_MS = 60_000;
/** How long "Kom og spil" waits for an answer. */
export const INVITE_TTL_MS = 2 * 60_000;
/** One "Kom og spil" a minute to the same friend. */
export const INVITE_INTERVAL_MS = 60_000;
/** A player on the start page counts as there this long after it last asked for the list. */
export const START_PAGE_PRESENCE_MS = 30_000;

/** Where a friend is right now. Never the room code. */
export type FriendWhere =
  | { status: 'offline' }
  | { status: 'start' }
  | { status: 'playing'; gameId: GameId; visibility: RoomVisibility };

export interface FriendInfo {
  id: PlayerId;
  displayName: string;
  where: FriendWhere;
}

export interface FriendRef {
  id: PlayerId;
  displayName: string;
}

/** "Kom og spil": a friend would like the player in their room. */
export interface FriendInvite {
  from: FriendRef;
  gameId: GameId;
  sentAt: number;
}

/** Everything the friends list shows. */
export interface FriendsOverview {
  friends: FriendInfo[];
  /** Requests to the player that wait for a yes or a no. */
  incoming: FriendRef[];
  /** Players the player has asked, who have not answered yet. */
  outgoing: PlayerId[];
  invites: FriendInvite[];
}

export type FriendRequestResponse =
  | { ok: true; outcome: 'sent' | 'friends' }
  | { ok: false; error: 'NOT_IN_ROOM' | 'TOO_MANY_FRIENDS' | 'TOO_MANY_REQUESTS' };

/** What the server tells a player in a room about friends, as it happens. */
export type FriendPush =
  | { type: 'request'; from: FriendRef }
  | { type: 'invite'; invite: FriendInvite }
  | { type: 'knock'; from: FriendRef }
  /** The knock was answered elsewhere or timed out, so the card can go. */
  | { type: 'knockGone'; playerId: PlayerId }
  /** Someone said yes or removed the player: fetch the list again. */
  | { type: 'changed' };

const playerIdSchema = z.string().min(1).max(64);

export const friendRequestSchema = z.object({ playerId: playerIdSchema });

export const friendAnswerSchema = z.object({ accept: z.boolean() });

export const joinFriendRequestSchema = z.object({
  gameId: z.string().regex(/^[a-z0-9-]{1,40}$/),
  friendId: playerIdSchema,
});

export const knockAnswerSchema = z.object({ playerId: playerIdSchema, allow: z.boolean() });

export type JoinFriendRequest = z.infer<typeof joinFriendRequestSchema>;
export type KnockAnswer = z.infer<typeof knockAnswerSchema>;
